/**
 * One collapsible module section of the notification centre
 * (Purchasing, Quality, Documents & Compliance, Onboarding, RFQ, System).
 *
 * Carries the per-group affordances: unread and action-required counts,
 * mark-the-group-read, and a mute control that writes a real per-user
 * subscription row at domain scope.
 *
 * Rows inside are split into subdomain blocks (Purchase Orders, NCRs, ...) so
 * a busy module stays readable; a section with a single block renders the
 * block header only when it is named.
 */
import type { Notification } from '../../lib/api';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { CheckCheck, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { NotificationModuleGroup } from './groupNotifications';
import NotificationEntryRow from './NotificationEntryRow';
import { getSeverityMeta } from './severity';

export interface NotificationGroupSectionProps {
  group: NotificationModuleGroup;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMarkGroupRead: (group: NotificationModuleGroup) => void;
  selectedIds: Set<number>;
  onSelectionChange: (ids: number[], selected: boolean) => void;
  onOpen: (notification: Notification) => void;
  onMarkRead: (ids: number[]) => void;
  onDelete: (id: number) => void;
}

export default function NotificationGroupSection({
  group,
  open,
  onOpenChange,
  onMarkGroupRead,
  selectedIds,
  onSelectionChange,
  onOpen,
  onMarkRead,
  onDelete,
}: NotificationGroupSectionProps) {
  const DomainIcon = group.icon;
  const severityMeta = getSeverityMeta(group.highestSeverity);
  const isEmpty = group.totalCount === 0;

  return (
    <Card>
      <Collapsible open={open} onOpenChange={onOpenChange}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between gap-3">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex flex-1 items-center gap-3 text-left min-w-0"
                aria-expanded={open}
              >
                {open ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span className={cn('p-1.5 rounded-md shrink-0', severityMeta.chipClass)}>
                  <DomainIcon className="h-4 w-4" />
                </span>
                <span className="font-semibold truncate">{group.label}</span>
                <span className="text-sm text-muted-foreground shrink-0">
                  {group.totalCount}
                </span>
                {group.unreadCount > 0 && (
                  <Badge variant="default" className="text-xs shrink-0">
                    {group.unreadCount} unread
                  </Badge>
                )}
                {group.actionRequiredCount > 0 && (
                  <Badge
                    variant="outline"
                    className={cn('text-xs shrink-0', getSeverityMeta('action_required').badgeClass)}
                  >
                    {group.actionRequiredCount} needs action
                  </Badge>
                )}
              </button>
            </CollapsibleTrigger>

            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onMarkGroupRead(group)}
                disabled={group.unreadCount === 0}
                title={
                  group.unreadCount === 0
                    ? `No unread ${group.label} notifications`
                    : `Mark the ${group.label} notifications shown here as read`
                }
              >
                <CheckCheck className="h-4 w-4 mr-1.5" />
                Mark read
              </Button>
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="p-0">
            {isEmpty ? (
              <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                {group.emptyHint}
              </div>
            ) : (
              group.subgroups.map((subgroup) => (
                <div key={subgroup.key} className="border-t border-border first:border-t-0">
                  <div className="flex items-center justify-between gap-2 px-4 py-2 bg-muted/40">
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {subgroup.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {subgroup.unreadCount > 0
                        ? `${subgroup.unreadCount} unread of ${subgroup.totalCount}`
                        : `${subgroup.totalCount} total`}
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {subgroup.entries.map((entry) => (
                      <NotificationEntryRow
                        key={entry.key}
                        entry={entry}
                        selectedIds={selectedIds}
                        onSelectionChange={onSelectionChange}
                        onOpen={onOpen}
                        onMarkRead={onMarkRead}
                        onDelete={onDelete}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
